"use client"

import type React from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card"
import { Badge } from "./ui/badge" // Import our new Badge component
import { GradeBall } from "./ui/gradeBall"
import { Collapsible, CollapsibleContent, CollapsibleTrigger, CollapsibleArrow } from "./ui/collapsible"
import { CheckCircle } from "lucide-react" // Change Unlock to CheckCircle
import { BlurredContent } from "./BlurredContent"
import Link from "next/link"
import { useSchoolAccess } from "../contexts/SchoolAccessContext" // Add this import

function createSeoSlug(name: string, skolkod: string): string {
  const nameSlug = name
    .toLowerCase()
    .replace(/å/g, "a")
    .replace(/ä/g, "a")
    .replace(/ö/g, "o")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
  return `${nameSlug}-${skolkod}`
}

interface School {
  skolenhetskod: string
  name: string
  kommun: string
  grades: string[]
  totalStudents: number
  studentsPerTeacher: number
  qualifiedTeachers: number | "#N/A" | string | null
  grades9: string
  testResults: {
    sv3: TestResult
    ma3: TestResult
    sv6: TestResult
    ma6: TestResult
    en6: TestResult
    sv9: TestResult
    ma9: TestResult
    en9: TestResult
  }
  surveyResults5: SurveyResult[]
  surveyResults8: SurveyResult[]
}

interface TestResult {
  score: number | "n/a"
  percentile: number | "n/a"
}

interface SurveyResult {
  dimension: string
  score: number
}

interface SchoolCardProps {
  school: School
  searchParams: { [key: string]: string | string[] | undefined }
}

const averageQualifiedTeachers = 73

const surveyAverages = {
  5: {
    "Information om utbildningen": 6.7,
    Stimulans: 6.5,
    Stöd: 7.4,
    "Kritiskt tänkande": 6.5,
    "Bemötande - lärare": 7.4,
    "Bemötande - elever": 6.9,
    Inflytande: 5.8,
    Studiero: 5.6,
    Trygghet: 8.1,
    "Förhindra kränkningar": 6.3,
    Elevhälsa: 6.8,
    "Övergripande nöjdhet": 6.9,
  },
  8: {
    "Information om utbildningen": 5.3,
    Stimulans: 5.4,
    Stöd: 6.4,
    "Kritiskt tänkande": 5.7,
    "Bemötande - lärare": 6.7,
    "Bemötande - elever": 6.5,
    Inflytande: 4.6,
    Studiero: 5.6,
    Trygghet: 7.8,
    "Förhindra kränkningar": 5.4,
    Elevhälsa: 5.8,
    "Övergripande nöjdhet": 5.8,
  },
}

export const SchoolCard: React.FC<SchoolCardProps> = ({ school, searchParams }) => {
  const { hasAccess } = useSchoolAccess()

  const gradeNumbers = school.grades
    .map((grade) => {
      if (grade === null || grade === undefined) return null;
      if (typeof grade === "string") {
        if (grade.toLowerCase() === "f") return "F";
        const match = grade.match(/^\d+$/);
        if (match) return Number(match[0]);
        return null;
      }
      if (typeof grade === "number") return grade;
      return null;
    })
    .filter((grade): grade is number | "F" => grade !== null);

  const renderTestResults = () => {
    const results = [
      { name: "Svenska åk 3", result: school.testResults.sv3, maxScore: 100, average: 93 },
      { name: "Matematik åk 3", result: school.testResults.ma3, maxScore: 100, average: 90 },
      { name: "Svenska åk 6", result: school.testResults.sv6, maxScore: 20, average: 12.8 },
      { name: "Matematik åk 6", result: school.testResults.ma6, maxScore: 20, average: 11.8 },
      { name: "Engelska åk 6", result: school.testResults.en6, maxScore: 20, average: 16.5 },
      { name: "Svenska åk 9", result: school.testResults.sv9, maxScore: 20, average: 13.7 },
      { name: "Matematik åk 9", result: school.testResults.ma9, maxScore: 20, average: 12.2 },
      { name: "Engelska åk 9", result: school.testResults.en9, maxScore: 20, average: 16.2 },
    ]

    const renderResultContent = (item: (typeof results)[0]) => (
      <div>
        <div className="relative h-6">
          <div className="absolute inset-0 bg-gray-200 rounded-full"></div>
          <div
            className="absolute inset-y-0 left-0 bg-blue-600 rounded-full flex items-center justify-end pr-2"
            style={{ width: `${(Number(item.result.score) / item.maxScore) * 100}%` }}
          >
            <span className="text-white text-sm font-semibold">{item.result.score}</span>
          </div>
          <div
            className="absolute inset-y-0 border-l-2 border-dashed border-yellow-400"
            style={{ left: `${(item.average / item.maxScore) * 100}%` }}
          ></div>
        </div>
        <div className="mt-1 text-xs text-gray-500 text-right">Genomsnitt: {item.average}</div>
      </div>
    )

    return (
      <>
        {results.map((item, index) => (
          <div key={index} className="mb-4">
            <p className="text-sm font-medium text-gray-700 mb-1">{item.name}</p>
            {item.result && item.result.score !== "n/a" ? (
              item.name.toLowerCase().includes("matematik") || item.name.toLowerCase().includes("engelska") ? (
                <BlurredContent schoolId={school.skolenhetskod} schoolName={school.name}>
                  {renderResultContent(item)}
                </BlurredContent>
              ) : (
                renderResultContent(item)
              )
            ) : (
              <p className="text-sm text-gray-500">N/A</p>
            )}
          </div>
        ))}
        <p className="text-xs text-gray-500 mt-4">
          Blå stapel är skolans värde. Gul linje är medelvärde för alla skolor.
        </p>
      </>
    )
  }

  const renderSurveyResults = (results: SurveyResult[], grade: 5 | 8) => {
    const averages = surveyAverages[grade]
    const blurredParams = {
      5: [
        "Kritiskt tänkande",
        "Bemötande - elever",
        "Bemötande - lärare",
        "Inflytande",
        "Förhindra kränkningar",
        "Elevhälsa",
        "Övergripande nöjdhet",
      ],
      8: [
        "Kritiskt tänkande",
        "Bemötande - elever",
        "Bemötande - lärare",
        "Inflytande",
        "Förhindra kränkningar",
        "Elevhälsa",
        "Övergripande nöjdhet",
      ],
    }

    return (
      <>
        {results.map((item, index) => {
          const average = averages[item.dimension as keyof typeof averages]
          const isBlurred = blurredParams[grade].includes(item.dimension)

          const content = (
            <div>
              <div className="relative h-6">
                <div className="absolute inset-0 bg-gray-200 rounded-full"></div>
                <div
                  className="absolute inset-y-0 left-0 bg-blue-600 rounded-full flex items-center justify-end pr-2"
                  style={{ width: `${(item.score / 10) * 100}%` }}
                >
                  <span className="text-white text-sm font-semibold">{item.score.toFixed(1)}</span>
                </div>
                {average !== undefined && (
                  <div
                    className="absolute inset-y-0 border-l-2 border-dashed border-yellow-400"
                    style={{ left: `${(average / 10) * 100}%` }}
                  ></div>
                )}
              </div>
              <div className="mt-1 text-xs text-gray-500 text-right">
                Genomsnitt: {average !== undefined ? average.toFixed(1) : "N/A"}
              </div>
            </div>
          )

          return (
            <div key={index} className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-1">{item.dimension}</p>
              {isBlurred ? (
                <BlurredContent schoolId={school.skolenhetskod} schoolName={school.name}>
                  {content}
                </BlurredContent>
              ) : (
                content
              )}
            </div>
          )
        })}
        <p className="text-xs text-gray-500 mt-4">
          {`Resultat från elevenkät årskurs ${grade}.`} Skala 0-10. Blå stapel är skolans värde. Gul linje är medelvärde
          för alla skolor.
        </p>
      </>
    )
  }

  return (
    <Card className="w-full relative">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>{school.name}</CardTitle>
            <CardDescription>{school.kommun}</CardDescription>
          </div>
          {hasAccess && (
            <Badge variant="success" className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3" />
              <span>Upplåst</span>
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-sm font-medium text-gray-500 mb-2">Årskurser:</p>
          <div className="flex flex-wrap gap-2">
            {gradeNumbers.map((grade) => (
              <GradeBall key={grade} grade={grade} />
            ))}
          </div>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-500 mb-2">
            Antal elever: ca {Math.round(school.totalStudents / 10) * 10}
          </p>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-500 mb-2">Elever per lärare: {school.studentsPerTeacher}</p>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-500 mb-2">Andel behöriga lärare:</p>
          {school.qualifiedTeachers !== null &&
          school.qualifiedTeachers !== "#N/A" &&
          school.qualifiedTeachers !== "n/a" ? (
            <>
              <div className="relative h-6">
                <div className="absolute inset-0 bg-gray-200 rounded-full"></div>
                <div
                  className="absolute inset-y-0 left-0 bg-blue-600 rounded-full flex items-center justify-end pr-2"
                  style={{ width: `${school.qualifiedTeachers}%` }}
                >
                  <span className="text-white text-sm font-semibold">{school.qualifiedTeachers}%</span>
                </div>
                <div
                  className="absolute inset-y-0 border-l-2 border-dashed border-yellow-400"
                  style={{ left: `${averageQualifiedTeachers}%` }}
                ></div>
              </div>
              <div className="mt-1 text-xs text-gray-500 text-right">Genomsnitt: {averageQualifiedTeachers}%</div>
            </>
          ) : (
            <p className="text-sm">n/a</p>
          )}
        </div>
        <div>
          <p className="text-sm font-medium text-gray-500 mb-2">
            Genomsnittligt meritvärde årskurs 9 (max 340): {school.grades9 || "N/A"}
          </p>
        </div>
        <Collapsible>
          <CollapsibleTrigger asChild>
            <button className="w-full text-left bg-blue-100 hover:bg-blue-200 transition-colors p-4 flex justify-between items-center rounded-lg">
              <span className="text-lg font-semibold text-gray-800">Nationella prov</span>
              <CollapsibleArrow />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="p-4 space-y-4 bg-gray-50 rounded-b-lg border border-t-0 border-gray-300">
              {renderTestResults()}
            </div>
          </CollapsibleContent>
        </Collapsible>
        <Collapsible>
          <CollapsibleTrigger asChild>
            <button className="w-full text-left bg-blue-100 hover:bg-blue-200 transition-colors p-4 flex justify-between items-center rounded-lg">
              <span className="text-lg font-semibold text-gray-800">Elevenkät årskurs 5</span>
              <CollapsibleArrow />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="p-4 space-y-4 bg-gray-50 rounded-b-lg border border-t-0 border-gray-300">
              {renderSurveyResults(school.surveyResults5, 5)}
            </div>
          </CollapsibleContent>
        </Collapsible>
        <Collapsible>
          <CollapsibleTrigger asChild>
            <button className="w-full text-left bg-blue-100 hover:bg-blue-200 transition-colors p-4 flex justify-between items-center rounded-lg">
              <span className="text-lg font-semibold text-gray-800">Elevenkät årskurs 8</span>
              <CollapsibleArrow />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="p-4 space-y-4 bg-gray-50 rounded-b-lg border border-t-0 border-gray-300">
              {renderSurveyResults(school.surveyResults8, 8)}
            </div>
          </CollapsibleContent>
        </Collapsible>
        <div className="flex justify-center">
          <Link
            href={{
              pathname: `/skola/enhet/${createSeoSlug(school.name, school.skolenhetskod)}`,
              query: { 
                back: Object.entries(searchParams)
                  .map(([key, value]) => {
                    if (Array.isArray(value)) {
                      return value.map(v => `${key}=${encodeURIComponent(v)}`).join('&')
                    } else if (value !== undefined) {
                      return `${key}=${encodeURIComponent(value)}`
                    }
                    return ''
                  })
                  .filter(Boolean)
                  .join('&')
              },
            }}
            className="mt-4 inline-block bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
          >
            Visa mer information
          </Link>
        </div>
      </CardContent>
    </Card>
  )
}

